import { useEffect, lazy, Suspense } from 'react';
import type { Role } from './stores/authStore';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { RoleRoute } from './components/layout/RoleRoute';
import { InicioSegunRol } from './components/layout/InicioSegunRol';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Employees = lazy(() => import('./pages/Employees'));
const Schedules = lazy(() => import('./pages/Schedules'));
const Clients = lazy(() => import('./pages/Clients'));
const Services = lazy(() => import('./pages/Services'));
const Login = lazy(() => import('./pages/Login'));
const LogisticsMap = lazy(() => import('./pages/LogisticsMap'));
const Attendance = lazy(() => import('./pages/Attendance'));
const Billing = lazy(() => import('./pages/Billing'));
const Reports = lazy(() => import('./pages/Reports'));
const Nomina = lazy(() => import('./pages/Nomina'));
const Settings = lazy(() => import('./pages/Settings'));
const SupervisorMobile = lazy(() => import('./pages/SupervisorMobile'));

const PageLoader = () => (
  <div className="flex flex-col h-screen w-full items-center justify-center bg-background/50 backdrop-blur-sm">
    <Loader2 className="h-10 w-10 animate-spin text-primary" />
    <span className="text-sm text-muted-foreground mt-4 font-medium animate-pulse">Cargando módulo...</span>
  </div>
);
import { supabase } from './lib/supabase';
import { useAuthStore } from './stores/authStore';

function App() {
  const setUser = useAuthStore((s) => s.setUser);
  const setRole = useAuthStore((s) => s.setRole);
  const setLoading = useAuthStore((s) => s.setLoading);
  const user = useAuthStore((s) => s.user);
  const role = useAuthStore((s) => s.role);

  useEffect(() => {
    let mounted = true;

    async function loadRole(userId: string) {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('rol')
          .eq('id', userId)
          .single();

        if (!mounted) return;

        if (!error && data) {
          setRole(data.rol as Role);
        } else {
          console.error("Failed to fetch profile role:", error?.message ?? 'Unknown error');
          // Default to lowest priviledge if failed but session exists to prevent infinite load
          setRole('funcionario');
        }
      } catch (err) {
        console.error("Failed to fetch profile role:", err instanceof Error ? err.message : 'Unknown error');
        if (mounted) setRole('funcionario');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    async function initSession() {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error("Session fetch error:", error instanceof Error ? error.message : String(error));
          // If the session is invalid or corrupted (which causes the need to clear cookies), force sign out
          localStorage.removeItem('soclean-coordinacion-auth'); // Specifically clear the bad token cache
          await supabase.auth.signOut().catch(() => { });
          if (mounted) {
            setUser(null);
            setRole(null);
            setLoading(false);
          }
          return;
        }

        if (mounted) {
          setUser(session?.user ?? null);
        }

        if (session?.user) {
          await loadRole(session.user.id);
        }
      } catch (err) {
        console.error("Critical Auth Init Error:", err instanceof Error ? err.message : 'Unknown error');
        try {
            await supabase.auth.signOut();
        } catch (signOutErr) {
            console.error("SignOut error during critical failure:", signOutErr instanceof Error ? signOutErr.message : 'Unknown error');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    void initSession();

    // Listen for auth changes.
    // El callback es SÍNCRONO y no llama a supabase.*: el cliente mantiene un lock interno
    // mientras lo ejecuta, así que cualquier consulta acá adentro se deadlockea (la promesa
    // nunca resuelve y congela toda la app). El fetch del perfil se difiere con setTimeout
    // para que corra una vez liberado el lock.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;

      if (event === 'SIGNED_OUT' || !session) {
        setUser(null);
        setRole(null);
        setLoading(false);
        return;
      }

      setUser(session.user);

      // TOKEN_REFRESHED: la sesión sigue activa y el rol ya está cargado.
      // INITIAL_SESSION: initSession() ya se encarga de cargar el perfil.
      if (event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') return;

      const userId = session.user.id;
      setTimeout(() => {
        if (mounted) void loadRole(userId);
      }, 0);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [setUser, setRole, setLoading]);

  return (
    <Router>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route
            path="/login"
            element={user ? <Navigate to="/" replace /> : <Login />}
          />

          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<DashboardLayout />}>
              <Route index element={<InicioSegunRol dashboard={<Dashboard />} supervisor={<SupervisorMobile />} />} />
              <Route path="supervisor" element={role?.toLowerCase() === 'supervisor' ? <SupervisorMobile /> : <Navigate to="/" replace />} />

              {/* Cada pantalla detrás de su guarda. El menú oculta lo que el rol
                  no debería ver, pero ocultar no protege: sin guarda se entra
                  por URL. Ambos leen la misma tabla ACCESO_PANTALLA. */}
              <Route element={<RoleRoute pantalla="funcionarios" />}>
                <Route path="funcionarios" element={<Employees />} />
              </Route>
              <Route element={<RoleRoute pantalla="horarios" />}>
                <Route path="horarios" element={<Schedules />} />
              </Route>
              <Route element={<RoleRoute pantalla="clientes" />}>
                <Route path="clientes" element={<Clients />} />
              </Route>
              <Route element={<RoleRoute pantalla="servicios" />}>
                <Route path="servicios" element={<Services />} />
              </Route>
              <Route element={<RoleRoute pantalla="logistica" />}>
                <Route path="logistica" element={<LogisticsMap />} />
              </Route>
              <Route element={<RoleRoute pantalla="asistencia" />}>
                <Route path="asistencia" element={<Attendance />} />
              </Route>
              <Route element={<RoleRoute pantalla="reportes" />}>
                <Route path="reportes" element={<Reports />} />
              </Route>
              <Route element={<RoleRoute pantalla="facturacion" />}>
                <Route path="facturacion" element={<Billing />} />
              </Route>
              <Route element={<RoleRoute pantalla="nomina" />}>
                <Route path="nomina" element={<Nomina />} />
              </Route>
              <Route element={<RoleRoute pantalla="configuracion" />}>
                <Route path="configuracion" element={<Settings />} />
              </Route>

              {/* Catch-all to 404/Dashboard inside Layout */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
      <Analytics />
      <SpeedInsights />
    </Router>
  );
}

export default App;
